import { useMemo, useState } from 'react'
import { Modal } from './Modal'
import { Thumb } from './Thumb'
import { componentLine, groupComponents, mealMatches } from '../lib/meals'
import { DAY_NAMES, type Category, type Item, type Lunch, type Meal } from '@shared/types'

interface Props {
  lunch: Lunch
  meals: Meal[]
  items: Item[]
  categories: Category[]
  onApply: (mealId: string, mode: 'replace' | 'add') => void
  /**
   * Save the day as it currently stands; resolves false when the name was
   * refused, so the typed name is not thrown away. Absent on an empty day.
   */
  onSaveDay: ((name: string) => Promise<boolean>) | null
  onToggleFavorite: (meal: Meal) => void
  onManage: () => void
  onClose: () => void
}

/**
 * Fill a day from a saved meal, or save the day as one.
 *
 * Both directions live in the same modal because they are the same thought:
 * you are either reaching for a combination that already works, or you have
 * just built one and want it back next week.
 */
export function MealPicker({
  lunch,
  meals,
  items,
  categories,
  onApply,
  onSaveDay,
  onToggleFavorite,
  onManage,
  onClose,
}: Props) {
  const [query, setQuery] = useState('')
  const [naming, setNaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [saving, setSaving] = useState(false)

  /*
   * The picker stays open after a save. The new meal drops into the list
   * below, which is the confirmation that it landed -- and the next thing you
   * often want is to apply it to another day.
   */
  async function saveDay() {
    const trimmed = newName.trim()
    if (!trimmed || !onSaveDay || saving) return
    setSaving(true)
    if (await onSaveDay(trimmed)) {
      setNaming(false)
      setNewName('')
    }
    setSaving(false)
  }

  const itemsById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])
  const dayName = DAY_NAMES[lunch.dayOfWeek - 1]!
  const dayIsEmpty = lunch.items.length === 0

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return meals.filter((m) => mealMatches(m, itemsById, needle))
  }, [meals, itemsById, query])

  return (
    <Modal
      title={`Meals for ${dayName}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn sm ghost" onClick={onManage}>
            Manage meals
          </button>
          <div className="spacer" />
          <button className="btn" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      {onSaveDay &&
        (naming ? (
          <div className="meal-save">
            <label htmlFor="meal-save-name">Name this meal</label>
            <div className="row">
              <input
                id="meal-save-name"
                type="text"
                autoComplete="off"
                value={newName}
                placeholder={`${dayName}'s lunch`}
                maxLength={60}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveDay()
                }}
                autoFocus
              />
              <button className="btn primary" disabled={!newName.trim() || saving} onClick={saveDay}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className="btn ghost" onClick={() => setNaming(false)}>
                Cancel
              </button>
            </div>
            <p className="muted" style={{ fontSize: 12.5, margin: '6px 0 0' }}>
              Saves the {lunch.items.length} item{lunch.items.length === 1 ? '' : 's'} on {dayName} as
              they are now.
            </p>
          </div>
        ) : (
          <button className="btn" onClick={() => setNaming(true)}>
            ⭐ Save {dayName} as a meal
          </button>
        ))}

      {meals.length === 0 ? (
        <p className="muted">
          No saved meals yet. Build a day you like, then save it here — or create one from scratch on
          the Meals tab.
        </p>
      ) : (
        <>
          {meals.length > 6 && (
            <input
              className="pantry-search"
              type="search"
              autoComplete="off"
              placeholder="Search saved meals…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search saved meals"
            />
          )}

          {shown.length === 0 ? (
            <p className="muted">Nothing matches “{query}”.</p>
          ) : (
            <div className="meal-list">
              {shown.map((meal) => (
                <div className="meal-row" key={meal.id}>
                  <button
                    type="button"
                    className={`star ${meal.favorite ? 'on' : ''}`}
                    onClick={() => onToggleFavorite(meal)}
                    aria-pressed={meal.favorite}
                    aria-label={
                      meal.favorite ? `Unfavourite ${meal.name}` : `Make ${meal.name} a favourite`
                    }
                  >
                    {meal.favorite ? '★' : '☆'}
                  </button>
                  <div className="meal-row-body">
                    <span className="title">
                      {meal.emoji && <span aria-hidden="true">{meal.emoji} </span>}
                      {meal.name}
                    </span>
                    <span className="sub">{componentLine(meal, itemsById) || 'Nothing left in it'}</span>
                  </div>
                  <div className="meal-row-actions">
                    {dayIsEmpty ? (
                      <button className="btn sm primary" onClick={() => onApply(meal.id, 'replace')}>
                        Fill {dayName}
                      </button>
                    ) : (
                      <>
                        <button className="btn sm" onClick={() => onApply(meal.id, 'add')}>
                          Add
                        </button>
                        <button className="btn sm primary" onClick={() => onApply(meal.id, 'replace')}>
                          Replace
                        </button>
                      </>
                    )}
                  </div>
                  <div className="meal-thumbs">
                    {groupComponents(meal, itemsById, categories).flatMap((group) =>
                      group.items.map((item) => (
                        <Thumb
                          key={item.id}
                          emoji={item.emoji}
                          imageKey={item.imageKey}
                          name={item.name}
                        />
                      )),
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
